<template>
  <header :class="{'scrolled': isScrolled, 'hidden': isHidden}" class="header">
    <div class="container">
      <div class="logo">
        <router-link to="/" id="logoText">ZIMA</router-link>
        <span class="connection-status">
          <span :class="['status-dot', connectionStatus === 'CONNECTED' ? 'bg-green-400' : 'bg-yellow-400']"></span>
          {{ connectionStatus }}
        </span>
      </div>

      <div class="header-actions">
        <div class="auth-button">
          <div class="user-logout">
            <!-- Botão unificado para evitar mudanças de layout -->
            <!-- Container das notificacoes (movido para a direita do nome) -->
            <div class="notifications-container" @click="toggleNotifDropdown" v-if="false">
              <i class="fas fa-bell bell-icon"></i>
              <!-- Badge com número de notificações não lidas -->
              <span v-if="unreadCount > 0" class="notif-badge">{{ unreadCount }}</span>

              <!-- Dropdown das notificações -->
              <transition name="fade">
                <div v-if="isNotifOpen" class="notif-dropdown" @click.stop>
                  <ul>
                    <li v-for="(notif, idx) in limitedNotifications" :key="idx" class="notif-item">
                      <span v-if="notif.unread" class="notif-unread-dot"></span>
                      <div class="notif-text">
                        <p class="notif-title">{{ notif.title }}</p>
                        <p class="notif-time">{{ notif.time }}</p>
                      </div>
                    </li>
                  </ul>
                  <!-- Link para a pagina de notificacoes completas -->
                  <router-link to="/notifications" class="view-all">Ver tudo</router-link>
                </div>
              </transition>
            </div>

            <!-- Icone de usuario - hover abre o menu -->
            <div
              class="user-profile"
              :class="{ active: isMenuOpen }"
              @mouseenter="openMenu"
              @click="handleUserProfileClick"
            >
              <div class="avatar" :title="headerUsername">
                <img v-if="avatarUrl" :src="avatarUrl" alt="Avatar" />
                <span v-else>{{ userInitials }}</span>
              </div>
              <span class="username-text">{{ headerUsername }}</span>
            </div>

            <!-- Container das notificacoes (movido para a direita do nome) -->
            <div
              class="notifications-container"
              @click="toggleNotifDropdown"
              @mouseenter="cancelNotifClose"
              @mouseleave="scheduleNotifClose"
            >
              <i class="fas fa-bell bell-icon" aria-hidden="true"></i>
              <span v-if="unreadCount > 0" class="notif-badge" aria-label="Notificacoes nao lidas">{{ unreadCount }}</span>
              <transition name="fade">
                <div v-if="isNotifOpen" class="notif-dropdown" @click.stop>
                  <ul>
                    <li
                      v-for="(notif, idx) in limitedNotifications"
                      :key="idx"
                      class="notif-item"
                      role="button"
                      tabindex="0"
                      @click="openNotification(notif)"
                      @keydown.enter.prevent="openNotification(notif)"
                      @keydown.space.prevent="openNotification(notif)"
                    >
                      <span
                        class="notif-unread-dot"
                        :class="{ 'is-unread': notif.unread, 'is-read': !notif.unread }"
                      ></span>
                      <div class="notif-text">
                        <p class="notif-title">{{ notif.title }}</p>
                        <p class="notif-time">{{ notif.time }}</p>
                      </div>
                    </li>
                  </ul>
                  <router-link to="/notifications" class="view-all">Ver tudo</router-link>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Render the Menu when isMenuOpen is true -->
    <transition name="fade">
      <Menu
        v-if="isMenuOpen && !isMobile"
        @close-menu="toggleMenu"
        @start-close="scheduleMenuClose"
        @keep-open="cancelMenuClose"
      />
    </transition>
  </header>
</template>

<script>
import axios from 'axios';
import { API_BASE_URL } from '@/config/env';
import Menu from './Menu.vue'; // Import the Menu component

const NOTIF_STORAGE_KEY = 'notifications-data';
const PROFILE_AVATAR_KEY_PREFIX = 'profile_avatar_url';
const DEFAULT_AVATAR_URL = '/IMAGE-BASE.png';
const NOTIF_STREAM_RETRY_BASE_MS = 30000;
const NOTIF_STREAM_RETRY_MAX_MS = 5 * 60 * 1000;

const normalizeNotificationText = (value) => {
  return String(value ?? '').replace(/\batras\b/g, 'atrás');
};

const normalizeNotification = (notif) => {
  if (!notif || typeof notif !== 'object') return notif;
  const normalizedType = notif.type === 'alert' ? 'trade' : notif.type;
  let normalizedVariant = notif.variant;
  if (!normalizedVariant && normalizedType === 'trade') {
    normalizedVariant = 'trade-open';
  }
  if (notif.type === 'alert') {
    normalizedVariant = 'trade-stop';
  }

  return {
    ...notif,
    type: normalizedType,
    typeLabel: normalizedType === 'trade' ? 'Trade' : normalizeNotificationText(notif.typeLabel),
    variant: normalizedVariant,
    title: normalizeNotificationText(notif.title),
    description: normalizeNotificationText(notif.description),
    time: normalizeNotificationText(notif.time),
    createdAt: notif.createdAt || notif.created_at || null,
  };
};

const formatRelativeTime = (isoValue) => {
  const date = new Date(isoValue);
  if (!isoValue || Number.isNaN(date.getTime())) return 'agora';

  const diffMs = Date.now() - date.getTime();
  if (diffMs <= 0) return 'agora';

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diffMs < minute) return 'agora';
  if (diffMs < hour) return `${Math.floor(diffMs / minute)} min atrás`;
  if (diffMs < day) return `${Math.floor(diffMs / hour)}h atrás`;
  return `${Math.floor(diffMs / day)}d atrás`;
};

const loadNotifications = () => {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeNotification);
  } catch (e) {
    return [];
  }
};

const saveNotifications = (list) => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    // ignore storage errors (e.g., quota)
  }
};

const emitNotificationsUpdated = (list) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notifications-updated', { detail: list }));
};

const ensureWelcomeNotification = (list) => {
  return Array.isArray(list) ? list : [];
};

const buildAvatarStorageKey = (user) => {
  const token =
    (typeof localStorage !== 'undefined' && localStorage.getItem('token')) ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('token')) ||
    '';
  const storedUsername = typeof localStorage !== 'undefined' ? localStorage.getItem('username') : '';
  const identity =
    user?.id ||
    user?.user_id ||
    user?.email ||
    user?.wallet_address ||
    user?.account_id ||
    user?.username ||
    user?.user_name ||
    user?.name ||
    storedUsername ||
    token ||
    'guest';
  const normalized = String(identity).trim().toLowerCase().replace(/[^a-z0-9@._-]/g, '_');
  return `${PROFILE_AVATAR_KEY_PREFIX}:${normalized || 'guest'}`;
};

export default {
  name: 'AppHeader',
  data() {
    return {
      isScrolled: false,
      isHidden: false,
      lastScrollY: 0,
      isMenuOpen: false, // State to control the user menu visibility

      // Notificações de exemplo
      isNotifOpen: false, // Controla o dropdown de notificações
      notifCloseTimer: null,
      notifications: ensureWelcomeNotification(loadNotifications()),
      notifStreamAbort: null,
      notifStreamRetryTimer: null,
      notifStreamRetryDelayMs: NOTIF_STREAM_RETRY_BASE_MS,
      connectionStatus: 'CONNECTED',
      avatarUrl: '',
      closeMenuTimer: null,
      isMobile: false
    };
  },
  components: {
    Menu
  },
  computed: {
    // Verifica se o usuário está logado e em qual rota está
    isAuthenticated() {
      return !!localStorage.getItem('user-token');
    },
    isLoggedHome() {
      return this.$route.name === 'LoggedHome';
    },
    isSupport() {
      return this.$route.name === 'Support';
    },
    isCourses() {
      return this.$route.name === 'Courses';
    },
    isProfile() {
      return this.$route.name === 'Profile';
    },

    // Contador de notificações não lidas
    unreadCount() {
      return this.notifications.filter(n => n.unread).length;
    },
    // Lista limitada das últimas notificações (ex.: 3 ou 5)
    limitedNotifications() {
      return this.notifications.slice(0, 3);
    },
    userInitials() {
      const n = (this.headerUsername || '').replace(/[^a-zA-Z]/g, '');
      return (n.slice(0, 2) || 'US').toUpperCase();
    },
    headerUsername() {
      return this.$store.getters.username;
    },
  },
  methods: {
    getAuthToken() {
      return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    },
    async fetchCurrentUserProfile() {
      const token = this.getAuthToken();
      if (!token) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = response?.data;
        if (!user || typeof user !== 'object') return;
        this.$store.dispatch('setUser', user);
        this.syncAvatarFromStorage();
      } catch (e) {
        // fallback to cached store/local data when API is unavailable
      }
    },
    handleScroll() {
      const currentScrollY = window.scrollY;
      if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
        this.isHidden = true;
      } else {
        this.isHidden = false;
      }
      this.lastScrollY = currentScrollY;
      this.isScrolled = currentScrollY > 50;
    },
    handleVisibilityChange() {
      this.connectionStatus = document.hidden ? 'RECONNECTING' : 'CONNECTED';
      if (document.hidden) {
        this.stopNotificationsStream();
        return;
      }
      if (!document.hidden) {
        this.fetchNotificationsFromApi();
        this.startNotificationsStream();
      }
    },
    handleFocus() {
      this.connectionStatus = 'CONNECTED';
    },
    handleBlur() {
      this.connectionStatus = 'RECONNECTING';
    },
    logout() {
      this.stopNotificationsStream();
      localStorage.removeItem('user-token');
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('zima_show_welcome_once');
      localStorage.removeItem('notifications-data');
      this.$store.dispatch('clearUser');
      this.$store.dispatch('clearBybitConnection');
      window.dispatchEvent(new CustomEvent('notifications-updated', { detail: [] }));
      this.$router.replace({ name: 'Login' });
    },
    handleUserProfileClick() {
      if (this.isMobile) {
        this.$router.push({ name: 'Profile' });
        return;
      }
      this.toggleMenu();
    },
    toggleMenu() {
      this.cancelMenuClose();
      this.isMenuOpen = !this.isMenuOpen;
    },

    openMenu() {
      this.isMenuOpen = true;
      this.cancelMenuClose();
    },

    scheduleMenuClose() {
      this.cancelMenuClose();
      this.closeMenuTimer = window.setTimeout(() => {
        this.isMenuOpen = false;
      }, 260);
    },

    cancelMenuClose() {
      if (this.closeMenuTimer) {
        window.clearTimeout(this.closeMenuTimer);
        this.closeMenuTimer = null;
      }
    },
    // Close notifications dropdown on outside click
    _onGlobalClick(e) {
      const box = this.$el && this.$el.querySelector ? this.$el.querySelector('.notifications-container') : null;
      if (this.isNotifOpen && box && !box.contains(e.target)) {
        this.isNotifOpen = false;
      }
    },

    // Abre/fecha o dropdown de notificações
    toggleNotifDropdown() {
      this.cancelNotifClose();
      this.isNotifOpen = !this.isNotifOpen;
    },
    scheduleNotifClose() {
      this.cancelNotifClose();
      this.notifCloseTimer = window.setTimeout(() => {
        this.isNotifOpen = false;
      }, 220);
    },
    cancelNotifClose() {
      if (this.notifCloseTimer) {
        window.clearTimeout(this.notifCloseTimer);
        this.notifCloseTimer = null;
      }
    },
    syncHeaderUsername(name) {
      const safeName = name || this.$store.getters.username;
      this.$store.dispatch('setUsername', safeName);
    },
    handleUsernameEvent(e) {
      this.syncHeaderUsername(e && e.detail);
    },
    handleStorageUsername(e) {
      if (e.key === 'username' || e.key === 'user_profile') {
        this.$store.dispatch('hydrateFromStorage');
        this.syncAvatarFromStorage();
      }
    },
    syncAvatarFromStorage() {
      const user = this.$store?.state?.user || {};
      const avatarFromUser = user.avatar_url || user.avatarUrl || '';
      if (avatarFromUser) {
        this.avatarUrl = avatarFromUser;
        return;
      }
      if (typeof localStorage === 'undefined') {
        this.avatarUrl = DEFAULT_AVATAR_URL;
        return;
      }
      const key = buildAvatarStorageKey(user);
      this.avatarUrl = localStorage.getItem(key) || DEFAULT_AVATAR_URL;
    },
    handleAvatarUpdated(e) {
      if (e && typeof e.detail === 'string') {
        this.avatarUrl = e.detail;
        return;
      }
      this.syncAvatarFromStorage();
    },
    handleAvatarStorage(e) {
      if (e.key && e.key.startsWith(`${PROFILE_AVATAR_KEY_PREFIX}:`)) {
        this.syncAvatarFromStorage();
      }
    },
    syncNotificationsFromStorage() {
      this.notifications = ensureWelcomeNotification(loadNotifications());
    },
    markNotificationReadLocally(notif) {
      if (!notif) return;
      if (notif.id) {
        this.notifications = this.notifications.map(item =>
          item.id === notif.id ? { ...item, unread: false } : item
        );
      } else {
        this.notifications = this.notifications.map(item =>
          item.eventKey === notif.eventKey ? { ...item, unread: false } : item
        );
      }
      saveNotifications(this.notifications);
      emitNotificationsUpdated(this.notifications);
    },
    async syncNotificationReadApi(notif) {
      if (!notif?.id) return;
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;
      try {
        await axios.patch(`${API_BASE_URL}/api/v1/notifications/${notif.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        // Keep local UI state if API fails.
      }
    },
    async openNotification(notif) {
      if (!notif) return;
      if (notif.unread) {
        this.markNotificationReadLocally(notif);
        await this.syncNotificationReadApi(notif);
      }
      this.cancelNotifClose();
      this.isNotifOpen = false;
      if (this.$route.name !== 'Notifications') {
        this.$router.push({ name: 'Notifications' });
      }
    },
    async fetchNotificationsFromApi() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      try {
        const response = await axios.get(`${API_BASE_URL}/api/v1/notifications`, {
          params: { limit: 120 },
          headers: { Authorization: `Bearer ${token}` },
        });
        const rows = Array.isArray(response?.data?.notifications) ? response.data.notifications : [];
        const normalized = ensureWelcomeNotification(rows.map(item => normalizeNotification({
          ...item,
          eventKey: item.event_key,
          typeLabel: item.type_label,
          createdAt: item.created_at,
          time: formatRelativeTime(item.created_at),
        })));
        this.notifications = normalized;
        saveNotifications(normalized);
        emitNotificationsUpdated(normalized);
      } catch (e) {
        // Keep local cache if API is unavailable.
      }
    },
    handleNotificationsStreamFrame(frame) {
      const lines = String(frame || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      if (!lines.length) return;

      let eventType = 'message';
      let dataRaw = '';
      lines.forEach((line) => {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataRaw += line.slice(5).trim();
        }
      });

      if (eventType !== 'notification') return;
      try {
        JSON.parse(dataRaw || '{}');
      } catch (_) {
        return;
      }
      this.fetchNotificationsFromApi();
    },
    scheduleNotificationsStreamReconnect() {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      if (this.notifStreamRetryTimer) {
        window.clearTimeout(this.notifStreamRetryTimer);
      }
      this.notifStreamRetryTimer = window.setTimeout(() => {
        this.startNotificationsStream();
      }, this.notifStreamRetryDelayMs);
      this.notifStreamRetryDelayMs = Math.min(this.notifStreamRetryDelayMs * 2, NOTIF_STREAM_RETRY_MAX_MS);
    },
    stopNotificationsStream() {
      if (this.notifStreamAbort) {
        this.notifStreamAbort.abort();
        this.notifStreamAbort = null;
      }
      if (this.notifStreamRetryTimer) {
        window.clearTimeout(this.notifStreamRetryTimer);
        this.notifStreamRetryTimer = null;
      }
    },
    async startNotificationsStream() {
      this.stopNotificationsStream();
      if (typeof document !== 'undefined' && document.hidden) return;

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;

      const controller = new AbortController();
      this.notifStreamAbort = controller;

      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/notifications/stream`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          cache: 'no-store',
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error('stream_unavailable');
        }

        this.notifStreamRetryDelayMs = NOTIF_STREAM_RETRY_BASE_MS;
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        let done = false;
        while (!done) {
          const chunk = await reader.read();
          done = chunk.done;
          if (done) break;
          const value = chunk.value;
          buffer += decoder.decode(value, { stream: true });

          let sepIndex = buffer.indexOf('\n\n');
          while (sepIndex !== -1) {
            const frame = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            this.handleNotificationsStreamFrame(frame);
            sepIndex = buffer.indexOf('\n\n');
          }
        }
      } catch (_) {
        if (controller.signal.aborted) return;
        this.scheduleNotificationsStreamReconnect();
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 767;
      if (this.isMobile) {
        this.isMenuOpen = false;
      }
    },
    handleNotificationsUpdated(e) {
      if (e && Array.isArray(e.detail)) {
        this.notifications = e.detail;
      } else {
        this.syncNotificationsFromStorage();
      }
    },
    handleNotificationsStorage(e) {
      if (e.key === NOTIF_STORAGE_KEY) {
        this.syncNotificationsFromStorage();
      }
    },
  },
  watch: {
    '$store.state.user'() {
      this.syncAvatarFromStorage();
    },
    $route() {
      // Fecha o menu de usuário e o dropdown de notificações ao trocar de rota
      this.isMenuOpen = false;
      this.isNotifOpen = false;
      this.isHidden = false;
      this.cancelNotifClose();
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    // Define status com base na visibilidade da aba e foco da janela
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('blur', this.handleBlur);
    // estado inicial
    this.handleVisibilityChange();
    // close notifications dropdown on outside click
    document.addEventListener('click', this._onGlobalClick);
    this.$store.dispatch('hydrateFromStorage');
    this.syncAvatarFromStorage();
    this.fetchCurrentUserProfile();
    window.addEventListener('profile-username-updated', this.handleUsernameEvent);
    window.addEventListener('profile-avatar-updated', this.handleAvatarUpdated);
    window.addEventListener('storage', this.handleStorageUsername);
    window.addEventListener('storage', this.handleAvatarStorage);
    window.addEventListener('notifications-updated', this.handleNotificationsUpdated);
    window.addEventListener('storage', this.handleNotificationsStorage);
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    this.fetchNotificationsFromApi();
    this.startNotificationsStream();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleFocus);
    window.removeEventListener('blur', this.handleBlur);
    document.removeEventListener('click', this._onGlobalClick);
    window.removeEventListener('profile-username-updated', this.handleUsernameEvent);
    window.removeEventListener('profile-avatar-updated', this.handleAvatarUpdated);
    window.removeEventListener('storage', this.handleStorageUsername);
    window.removeEventListener('storage', this.handleAvatarStorage);
    window.removeEventListener('notifications-updated', this.handleNotificationsUpdated);
    window.removeEventListener('storage', this.handleNotificationsStorage);
    window.removeEventListener('resize', this.handleResize);
    this.stopNotificationsStream();
    this.cancelMenuClose();
    this.cancelNotifClose();
  },
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

* {
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  font-family: 'Montserrat', sans-serif;
  scroll-behavior: smooth;
}

:root {
  --primary-color: #005f73;
  --secondary-color: #94d2bd;
  --hover-color: #7394F0;
  --accent-color: #ffba08;
  --background-dark: #191e37;
  --background-light: #35374B;
  --dark-text: #f0ffffde;
  --button-hover-color: #50727B;
  --footer-bg: #191e37;
  --gold-shadow: rgba(255, 186, 8, 0.3);
  --transition-speed: 0.3s;

  --font-big: clamp(2.5rem, 5vw, 4.5rem);
  --font-medium: clamp(1.8rem, 4vw, 2.6rem);
  --font-small: 1.1rem;
}

body {
  background-color: #181a20;
  color: var(--dark-text);
  font-size: 16px;
}

header {
  background-color: #181a20;
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  transition: background-color var(--transition-speed), top 0.3s ease-in-out;
  padding: 15px 0;
}

header.hidden {
  top: -100px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: 0 30px;
}

.logo {
  display: flex;
  align-items: center;
}

#logoText {
  font-size: 28px;
  font-weight: 700;
  color: var(--dark-text);
  text-decoration: none;
  transition: color var(--transition-speed);
}

.logo:hover #logoText {
  color: var(--hover-color);
}

.header-actions {
  display: flex;
  align-items: center;
  justify-self: end; /* keep right cluster pinned */
}

.auth-button {
  display: flex;
  align-items: center;
}

.user-logout {
  display: flex;
  align-items: center;
  gap: 16px;
}
.user-icon {
  font-size: 1.5rem;
  color: var(--dark-text);
  gap: 30px;
  margin-left: 30px;
  cursor: pointer;
}

/* Notificações Container */
.notifications-container {
  position: relative;
  margin-left: 0;
  cursor: pointer;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
}

.notifications-container:hover {
  background-color: rgba(255, 255, 255, 0.06);
}

.bell-icon {
  font-size: 1.3rem;
  color: var(--dark-text);
  transition: color 0.3s;
}

.bell-icon:hover {
  color: var(--hover-color);
}

/* Badge de notificações não lidas */
.notif-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  background: #ff3b3b;
  color: #fff;
  width: 18px;
  height: 18px;
  aspect-ratio: 1 / 1;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  border-radius: 50%;
}

/* Dropdown de notificações */
.notif-dropdown {
  position: absolute;
  top: 30px;
  right: 0;
  background-color: #2e2e2e;
  border-radius: 8px;
  width: 280px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.5);
  z-index: 9999;
}

.notif-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.notif-item {
  border-bottom: 1px solid #444;
  padding: 8px 0;
  display: flex;
  align-items: center;
}

.notif-item:last-child {
  border-bottom: none;
}

.notif-text {
  display: flex;
  flex-direction: column;
  margin-left: 8px;
}

.notif-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
}

.notif-time {
  font-size: 0.8rem;
  color: #aaa;
  margin: 2px 0 0;
}

/* Link "Ver tudo" */
.view-all {
  display: block;
  text-align: center;
  color: #4e73df;
  margin-top: 10px;
  font-weight: 600;
  text-decoration: none;
}

.view-all:hover {
  text-decoration: underline;
}

/* Fade transition para o dropdown */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Connection status styles */
.connection-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 20px;
  font-size: 0.9rem;
  color: var(--dark-text);
  white-space: nowrap;
  min-width: 12ch; /* reserve space to avoid shifting when text changes */
}

.status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  display: inline-block;
  position: relative;
}

/* Fallbacks for Tailwind-like classes if Tailwind isn't present */
.bg-green-400 { background-color: #4ade80; }
.bg-yellow-400 { background-color: #facc15; }

/* Subtle glow matching the status color */
.status-dot.bg-green-400 { box-shadow: 0 0 8px #4ade80; }
.status-dot.bg-yellow-400 { box-shadow: 0 0 8px #facc15; }

/* Pulse effect */
.status-dot::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 999px;
  transform: translate(-50%, -50%) scale(1);
  background: inherit; /* uses the dot background color */
  opacity: 0.6;
  filter: blur(0.5px);
  animation: status-ping 1.6s ease-out infinite;
}

@keyframes status-ping {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  60% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
}

/* User profile (avatar + name) */
.user-profile {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-left: 0;
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  background: transparent;
  color: #0b1220;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.9rem;
  box-shadow: none;
  overflow: hidden;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 999px;
}

.username-text {
  color: var(--dark-text);
  font-weight: 600;
  font-size: 0.95rem;
}

.user-profile:hover .username-text,
.user-profile.active .username-text {
  color: var(--hover-color);
}

.username-text { transition: color 0.2s ease; }

/* Notifications dropdown – revamped styling */
.notif-dropdown {
  background: linear-gradient(180deg, #1d2230 0%, #1a1f2b 100%);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  width: 320px;
  padding: 8px 0 10px;
  box-shadow: 0 12px 30px rgba(0,0,0,0.45);
  overflow-y: auto;
  top: 44px;
  right: -6px;
}

.notif-dropdown::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 18px;
  border-width: 0 7px 7px 7px;
  border-style: solid;
  border-color: transparent transparent rgba(255,255,255,0.08) transparent;
}
.notif-dropdown::after {
  content: '';
  position: absolute;
  top: -5px;
  right: 18px;
  border-width: 0 7px 7px 7px;
  border-style: solid;
  border-color: transparent transparent #1d2230 transparent;
}

.notif-dropdown ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.notif-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  transition: background 0.25s ease;
}
.notif-item:last-child { border-bottom: none; }
.notif-item:hover { background: rgba(255,255,255,0.04); }

.notif-unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.notif-unread-dot.is-unread {
  background: #4e73df;
  box-shadow: 0 0 8px rgba(78,115,223,0.6);
  animation: notif-dot-pulse-strong 2.2s ease-in-out infinite;
}

.notif-unread-dot.is-read {
  background: #6b7280;
  box-shadow: 0 0 6px rgba(107,114,128,0.45);
  opacity: 0.9;
  animation: notif-dot-pulse-soft 3s ease-in-out infinite;
}

.notif-text { display: flex; flex-direction: column; gap: 2px; }
.notif-title { font-size: 0.95rem; font-weight: 600; color: #e9ecf3; margin: 0; }
.notif-time { font-size: 0.78rem; color: #9aa3b2; margin: 0; }

.view-all {
  display: block;
  width: fit-content;
  margin: 8px auto 2px;
  padding: 8px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, #4e73df 0%, #9b59b6 100%);
  color: #fff;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.view-all:hover { transform: translateY(-2px); opacity: 0.95; text-decoration: none; }

@keyframes notif-dot-pulse-strong {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.25); opacity: 1; }
}

@keyframes notif-dot-pulse-soft {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 0.9; }
}

/* Breathing Glow: static line + soft glowing blur that breathes */
.header::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: 0; height: 1px;
  background: linear-gradient(90deg, transparent, #4e73df, #9b59b6, transparent);
  opacity: 0.6;
}

.header::before {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: 0; height: 2px;
  background: linear-gradient(90deg, transparent, #4e73df, #9b59b6, transparent);
  filter: blur(2px);
  opacity: 0.35;
  animation: header-glow-breathe 2.4s ease-in-out infinite;
}

@keyframes header-glow-breathe {
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 0.6; }
}

@media (max-width: 767px) {
  header {
    padding: 10px 0;
  }

  .container {
    padding: 0 12px;
    grid-template-columns: 1fr auto;
  }

  #logoText {
    font-size: 22px;
  }

  .connection-status,
  .username-text {
    display: none;
  }

  .user-logout {
    gap: 8px;
  }

  .notif-dropdown {
    width: min(320px, calc(100vw - 20px));
    right: -2px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header::before { animation: none; }
}

/* Gentle bounce on notif badge */
.notif-badge { animation: notif-bounce 1.6s ease-in-out infinite; }
@keyframes notif-bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
</style>








